import React, { useState, useEffect } from 'react';
import { Plus, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { hasPI } from '../../../lib/permissions';
import { piApi } from '../../../services/piApi';
import '../PIShell.css';

const TYPE_COLORS = {
  revenue:     { bg: '#dcfce7', text: '#16a34a' },
  cost:        { bg: '#dbeafe', text: '#2563eb' },
  operational: { bg: '#fef3c7', text: '#d97706' },
  addon:       { bg: '#f3e8ff', text: '#9333ea' },
  talent:      { bg: '#ffe4e6', text: '#e11d48' },
  pricing:     { bg: '#e0f2fe', text: '#0284c7' },
  technology:  { bg: '#f1f5f9', text: '#475569' },
};

const STATUS_RAG = {
  not_started: { label: 'Not Started', cls: 'pi-badge-blue'  },
  in_progress: { label: 'In Progress', cls: 'pi-badge-amber' },
  at_risk:     { label: 'At Risk',     cls: 'pi-badge-red'   },
  complete:    { label: 'Complete',    cls: 'pi-badge-green' },
};

const TYPE_OPTIONS = ['revenue','cost','operational','addon','talent','pricing','technology'];

function InitiativeCard({ initiative, currentUser, onUpdate, canEdit }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState({});
  const rag = STATUS_RAG[initiative.status] || STATUS_RAG.not_started;
  const typeColor = TYPE_COLORS[initiative.type] || TYPE_COLORS.operational;
  const progressPct = initiative.target_value && initiative.actual_value
    ? Math.min(100, Math.round((initiative.actual_value / initiative.target_value) * 100))
    : 0;
  const isOverdue = initiative.due_date && new Date(initiative.due_date) < new Date() && initiative.status !== 'complete';

  const startEdit = () => {
    setForm({
      title: initiative.title || '',
      type: initiative.type || 'revenue',
      status: initiative.status || 'not_started',
      kpi_description: initiative.kpi_description || '',
      target_value: initiative.target_value ?? '',
      actual_value: initiative.actual_value ?? '',
      projected_ebitda_impact: initiative.projected_ebitda_impact ?? '',
      actual_ebitda_impact: initiative.actual_ebitda_impact ?? '',
      start_date: initiative.start_date ? initiative.start_date.slice(0, 10) : '',
      due_date: initiative.due_date ? initiative.due_date.slice(0, 10) : '',
      notes: initiative.notes || '',
    });
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await piApi.updateInitiative(initiative.portco_id, initiative.id, {
        ...form,
        target_value: form.target_value !== '' ? parseFloat(form.target_value) : null,
        actual_value: form.actual_value !== '' ? parseFloat(form.actual_value) : null,
        projected_ebitda_impact: form.projected_ebitda_impact !== '' ? parseFloat(form.projected_ebitda_impact) : null,
        actual_ebitda_impact: form.actual_ebitda_impact !== '' ? parseFloat(form.actual_ebitda_impact) : null,
        start_date: form.start_date || null,
        due_date: form.due_date || null,
      });
      setEditing(false);
      onUpdate();
    } catch (e) {
      console.error('[VCP] Update error:', e);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this initiative?')) return;
    setDeleting(true);
    try {
      await piApi.deleteInitiative(initiative.portco_id, initiative.id);
      onUpdate();
    } catch (e) {
      console.error('[VCP] Delete error:', e);
    } finally {
      setDeleting(false);
    }
  };

  const labelStyle = { fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 };
  const inputStyle = { width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };

  return (
    <div className="pi-card" style={{ marginBottom: 10 }}>
      <div
        style={{ padding: '14px 16px', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 12 }}
        onClick={() => setExpanded(e => !e)}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 6 }}>
            <span style={{ fontWeight: 600, fontSize: 14, color: '#1e293b' }}>{initiative.title}</span>
            <span style={{ background: typeColor.bg, color: typeColor.text, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10 }}>
              {initiative.type}
            </span>
            <span className={`pi-badge ${rag.cls}`} aria-label={`Status: ${rag.label}`}>{rag.label}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            {initiative.kpi_description && (
              <span style={{ fontSize: 12, color: '#64748b' }}>{initiative.kpi_description}</span>
            )}
            {initiative.target_value != null && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 140 }}>
                <div style={{ flex: 1, height: 5, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ width: `${progressPct}%`, height: '100%', borderRadius: 3, background: progressPct >= 100 ? '#16a34a' : '#4f46e5' }} />
                </div>
                <span style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>
                  {initiative.actual_value ?? 0} / {initiative.target_value}
                </span>
              </div>
            )}
            {initiative.projected_ebitda_impact != null && (
              <span style={{ fontSize: 12, color: '#475569' }}>
                Impact: ${initiative.projected_ebitda_impact}M EBITDA
              </span>
            )}
            {initiative.due_date && (
              <span style={{ fontSize: 12, color: isOverdue ? '#dc2626' : '#94a3b8', fontWeight: isOverdue ? 600 : 400 }}>
                Due {new Date(initiative.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                {isOverdue && ' (overdue)'}
              </span>
            )}
          </div>
        </div>
        <span style={{ color: '#94a3b8', flexShrink: 0 }}>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </span>
      </div>
      {expanded && (
        <div style={{ padding: '14px 16px', borderTop: '1px solid #f1f5f9' }}>
          {!editing ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 14 }}>
                <div>
                  <span style={labelStyle}>STATUS</span>
                  <span className={`pi-badge ${rag.cls}`}>{rag.label}</span>
                </div>
                <div>
                  <span style={labelStyle}>TYPE</span>
                  <span style={{ fontSize: 13, color: '#334155' }}>{initiative.type}</span>
                </div>
                <div>
                  <span style={labelStyle}>KPI</span>
                  <span style={{ fontSize: 13, color: '#334155' }}>{initiative.kpi_description || '—'}</span>
                </div>
                <div>
                  <span style={labelStyle}>TARGET VALUE</span>
                  <span style={{ fontSize: 13, color: '#334155' }}>{initiative.target_value ?? '—'}</span>
                </div>
                <div>
                  <span style={labelStyle}>ACTUAL VALUE</span>
                  <span style={{ fontSize: 13, color: '#334155' }}>{initiative.actual_value ?? '—'}</span>
                </div>
                <div>
                  <span style={labelStyle}>PROGRESS</span>
                  <span style={{ fontSize: 13, color: '#334155' }}>{progressPct}%</span>
                </div>
                <div>
                  <span style={labelStyle}>PROJECTED EBITDA IMPACT</span>
                  <span style={{ fontSize: 13, color: '#334155' }}>{initiative.projected_ebitda_impact != null ? `$${initiative.projected_ebitda_impact}M` : '—'}</span>
                </div>
                <div>
                  <span style={labelStyle}>ACTUAL EBITDA IMPACT</span>
                  <span style={{ fontSize: 13, color: '#334155' }}>
                    {initiative.actual_ebitda_impact != null ? `$${initiative.actual_ebitda_impact}M` : '—'}
                    {initiative.actual_ebitda_impact != null && initiative.projected_ebitda_impact != null && (
                      <span style={{ marginLeft: 6, color: initiative.actual_ebitda_impact >= initiative.projected_ebitda_impact ? '#16a34a' : '#dc2626', fontSize: 12 }}>
                        ({initiative.actual_ebitda_impact >= initiative.projected_ebitda_impact ? '+' : ''}
                        {Math.round(((initiative.actual_ebitda_impact / initiative.projected_ebitda_impact) - 1) * 100)}% vs plan)
                      </span>
                    )}
                  </span>
                </div>
                <div>
                  <span style={labelStyle}>HOLD PERIOD</span>
                  <span style={{ fontSize: 13, color: '#334155' }}>
                    {initiative.start_date ? new Date(initiative.start_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                    {' → '}
                    {initiative.due_date ? new Date(initiative.due_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                  </span>
                </div>
              </div>
              {initiative.notes && (
                <div style={{ marginBottom: 14 }}>
                  <span style={labelStyle}>NOTES</span>
                  <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.5, margin: 0 }}>{initiative.notes}</p>
                </div>
              )}
              {canEdit && (
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button className="pi-btn-ghost" onClick={(e) => { e.stopPropagation(); handleDelete(); }} disabled={deleting} style={{ color: '#dc2626', fontSize: 12 }}>
                    {deleting ? 'Deleting...' : 'Delete'}
                  </button>
                  <button className="pi-btn-primary" onClick={(e) => { e.stopPropagation(); startEdit(); }} style={{ fontSize: 12 }}>
                    Edit
                  </button>
                </div>
              )}
            </>
          ) : (
            <div onClick={e => e.stopPropagation()}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>TITLE *</label>
                  <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>TYPE</label>
                  <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} style={inputStyle}>
                    {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>STATUS</label>
                  <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} style={inputStyle}>
                    {Object.entries(STATUS_RAG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>KPI DESCRIPTION</label>
                  <input value={form.kpi_description} onChange={e => setForm(f => ({ ...f, kpi_description: e.target.value }))} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>TARGET VALUE</label>
                  <input type="number" value={form.target_value} onChange={e => setForm(f => ({ ...f, target_value: e.target.value }))} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>ACTUAL VALUE</label>
                  <input type="number" value={form.actual_value} onChange={e => setForm(f => ({ ...f, actual_value: e.target.value }))} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>PROJECTED EBITDA IMPACT ($M)</label>
                  <input type="number" value={form.projected_ebitda_impact} onChange={e => setForm(f => ({ ...f, projected_ebitda_impact: e.target.value }))} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>ACTUAL EBITDA IMPACT ($M)</label>
                  <input type="number" value={form.actual_ebitda_impact} onChange={e => setForm(f => ({ ...f, actual_ebitda_impact: e.target.value }))} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>START DATE</label>
                  <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>DUE DATE</label>
                  <input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} style={inputStyle} />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>NOTES</label>
                  <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} style={{ ...inputStyle, resize: 'vertical' }} />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="pi-btn-ghost" onClick={() => setEditing(false)}>Cancel</button>
                <button className="pi-btn-primary" onClick={handleSave} disabled={saving || !form.title}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AddInitiativeForm({ portcoId, onSaved, onCancel }) {
  const [form, setForm] = useState({
    title: '', type: 'revenue', status: 'not_started', kpi_description: '',
    target_value: '', actual_value: '', projected_ebitda_impact: '',
    actual_ebitda_impact: '', start_date: '', due_date: '', notes: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title) return;
    setSaving(true);
    try {
      await piApi.createInitiative(portcoId, {
        ...form,
        target_value: form.target_value !== '' ? parseFloat(form.target_value) : null,
        actual_value: form.actual_value !== '' ? parseFloat(form.actual_value) : null,
        projected_ebitda_impact: form.projected_ebitda_impact !== '' ? parseFloat(form.projected_ebitda_impact) : null,
        actual_ebitda_impact: form.actual_ebitda_impact !== '' ? parseFloat(form.actual_ebitda_impact) : null,
        start_date: form.start_date || null,
        due_date: form.due_date || null,
      });
      onSaved();
    } catch (e) {
      console.error('[VCP] Create initiative error:', e);
    } finally {
      setSaving(false);
    }
  };

  const labelStyle = { fontSize: 11, color: '#64748b', fontWeight: 600, display: 'block', marginBottom: 4 };
  const inputStyle = { width: '100%', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };

  return (
    <form onSubmit={handleSubmit} className="pi-card" style={{ padding: 18, marginBottom: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>TITLE *</label>
          <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            placeholder="Initiative title" required style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>TYPE</label>
          <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))} style={inputStyle}>
            {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>STATUS</label>
          <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} style={inputStyle}>
            {Object.entries(STATUS_RAG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>KPI DESCRIPTION</label>
          <input value={form.kpi_description} onChange={e => setForm(f => ({ ...f, kpi_description: e.target.value }))}
            placeholder="e.g. Revenue from new segment ($M)" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>TARGET VALUE</label>
          <input type="number" value={form.target_value} onChange={e => setForm(f => ({ ...f, target_value: e.target.value }))}
            placeholder="e.g. 5.0" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>ACTUAL VALUE</label>
          <input type="number" value={form.actual_value} onChange={e => setForm(f => ({ ...f, actual_value: e.target.value }))}
            placeholder="e.g. 3.2" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>PROJECTED EBITDA IMPACT ($M)</label>
          <input type="number" value={form.projected_ebitda_impact} onChange={e => setForm(f => ({ ...f, projected_ebitda_impact: e.target.value }))}
            placeholder="e.g. 1.5" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>ACTUAL EBITDA IMPACT ($M)</label>
          <input type="number" value={form.actual_ebitda_impact} onChange={e => setForm(f => ({ ...f, actual_ebitda_impact: e.target.value }))}
            placeholder="e.g. 1.2" style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>START DATE</label>
          <input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} style={inputStyle} />
        </div>
        <div>
          <label style={labelStyle}>DUE DATE</label>
          <input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} style={inputStyle} />
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={labelStyle}>NOTES</label>
          <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
            rows={2} placeholder="Rationale or context" style={{ ...inputStyle, resize: 'vertical' }} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" className="pi-btn-ghost" onClick={onCancel}>Cancel</button>
        <button type="submit" className="pi-btn-primary" disabled={saving}>
          {saving ? 'Saving...' : 'Add Initiative'}
        </button>
      </div>
    </form>
  );
}

export default function PIVCPTab({ portco, currentUser }) {
  const [initiatives, setInitiatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [aiDraft, setAiDraft] = useState(null);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [acceptingSet, setAcceptingSet] = useState(new Set());

  const canEdit = hasPI(currentUser, 'pi.vcp.edit');

  const loadInitiatives = () => {
    piApi.getInitiatives(portco.id)
      .then(r => setInitiatives(r.data || []))
      .catch(e => console.error('[VCP]', e))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadInitiatives(); }, [portco.id]);

  const handleDraftVCP = async () => {
    setAiLoading(true);
    try {
      const res = await piApi.draftVCP(portco.id);
      setAiDraft(res.data.initiatives || []);
    } catch (e) {
      console.error('[VCP] AI draft error:', e);
    } finally {
      setAiLoading(false);
    }
  };

  const handleAcceptDraft = async (draftItems, acceptAll = false) => {
    const indices = new Set(draftItems.map(d => aiDraft?.indexOf(d)).filter(i => i >= 0));
    setAcceptingSet(prev => new Set([...prev, ...indices]));
    try {
      for (const item of draftItems) {
        await piApi.createInitiative(portco.id, item);
      }
      if (acceptAll) {
        setAiDraft(null);
      } else {
        setAiDraft(prev => {
          const remaining = (prev || []).filter(d => !draftItems.includes(d));
          return remaining.length === 0 ? null : remaining;
        });
      }
      loadInitiatives();
    } catch (e) {
      console.error('[VCP] Accept draft error:', e);
    } finally {
      setAcceptingSet(prev => { const next = new Set(prev); indices.forEach(i => next.delete(i)); return next; });
    }
  };

  const filtered = initiatives.filter(i => {
    if (typeFilter !== 'all' && i.type !== typeFilter) return false;
    if (statusFilter !== 'all' && i.status !== statusFilter) return false;
    return true;
  });

  return (
    <div style={{ padding: '24px 28px' }}>
      {/* Top actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12.5, color: '#334155' }}>
            <option value="all">All Types</option>
            {TYPE_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 12.5, color: '#334155' }}>
            <option value="all">All Statuses</option>
            {Object.entries(STATUS_RAG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        {canEdit && (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="pi-ai-btn" onClick={handleDraftVCP} disabled={aiLoading} aria-label="Draft VCP with AI">
              <Sparkles size={14} />
              {aiLoading ? 'Analyzing...' : 'Draft VCP with AI'}
            </button>
            <button className="pi-btn-primary" onClick={() => setShowAddForm(s => !s)} aria-label="Add initiative">
              <Plus size={14} /> Add Initiative
            </button>
          </div>
        )}
      </div>

      {aiLoading && (
        <div className="pi-loading" style={{ marginBottom: 16 }}>
          <div className="pi-spinner" />
          <span>Analyzing your DD documents...</span>
        </div>
      )}

      {/* AI draft preview */}
      {aiDraft && (
        <div className="pi-card" style={{ marginBottom: 20, border: '1px solid #c7d2fe' }}>
          <div className="pi-card-header" style={{ background: '#eef2ff' }}>
            <span className="pi-card-title" style={{ color: '#4f46e5' }}>AI-Drafted VCP — {aiDraft.length} initiatives</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="pi-btn-ghost" onClick={() => setAiDraft(null)} disabled={acceptingSet.size > 0}>Discard</button>
              <button className="pi-ai-btn" onClick={() => handleAcceptDraft(aiDraft, true)} disabled={acceptingSet.size > 0}>
                {acceptingSet.size > 0 && acceptingSet.size === aiDraft?.length ? 'Accepting...' : 'Accept All'}
              </button>
            </div>
          </div>
          <div className="pi-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {aiDraft.map((item, i) => (
              <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ background: TYPE_COLORS[item.type]?.bg || '#f1f5f9', color: TYPE_COLORS[item.type]?.text || '#475569', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10 }}>{item.type}</span>
                  <span style={{ flex: 1, fontSize: 13, color: '#334155', fontWeight: 500 }}>{item.title}</span>
                  <span style={{ fontSize: 12, color: '#64748b' }}>${item.projected_ebitda_impact || 0}M</span>
                  <button className="pi-btn-ghost" style={{ fontSize: 11, padding: '4px 8px' }} onClick={() => handleAcceptDraft([item])} disabled={acceptingSet.has(i)}>
                    {acceptingSet.has(i) ? 'Accepting...' : 'Accept'}
                  </button>
                  <button className="pi-btn-ghost" style={{ fontSize: 11, padding: '4px 8px' }} onClick={() => setAiDraft(d => d.filter((_, j) => j !== i))} disabled={acceptingSet.has(i)}>Skip</button>
                </div>
                {item.dd_finding_reference && (
                  <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic', marginTop: 3, paddingLeft: 42 }}>
                    From DD: {item.dd_finding_reference}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {showAddForm && canEdit && (
        <AddInitiativeForm portcoId={portco.id} onSaved={() => { setShowAddForm(false); loadInitiatives(); }} onCancel={() => setShowAddForm(false)} />
      )}

      {/* Initiatives list */}
      {loading ? (
        <div className="pi-loading"><div className="pi-spinner" /><span>Loading initiatives...</span></div>
      ) : filtered.length === 0 ? (
        <div className="pi-empty">
          <div className="pi-empty-title">No initiatives yet</div>
          <div className="pi-empty-sub">Use AI to draft your VCP or add one manually.</div>
          {canEdit && (
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button className="pi-ai-btn" onClick={handleDraftVCP} disabled={aiLoading}><Sparkles size={13} /> Draft with AI</button>
              <button className="pi-btn-ghost" onClick={() => setShowAddForm(true)}><Plus size={13} /> Add manually</button>
            </div>
          )}
        </div>
      ) : (
        filtered.map(i => (
          <InitiativeCard key={i.id} initiative={i} currentUser={currentUser} onUpdate={loadInitiatives} canEdit={canEdit} />
        ))
      )}
    </div>
  );
}
